/**
 * useCloudTree Hook
 * 
 * Fetches the cloud code tree from Back4App API which includes:
 * - cloud/ folder: Cloud functions, triggers, jobs
 * - public/ folder: Static hosted files (HTML, CSS, JS, images)
 * 
 * The tree is returned in jsTree format and processed into usable arrays.
 * 
 * IMPORTANT: This hook parses the actual code content to extract:
 * - Parse.Cloud.define() calls -> Cloud Functions
 * - Parse.Cloud.beforeSave(), afterSave(), etc. -> Triggers
 * - Parse.Cloud.job() -> Jobs
 * 
 * It also extracts the individual code for each function/trigger.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  CloudTreeNode,
  CloudTreeResponse,
  ExtractedFile,
  PublicFile,
  CloudFunction,
  Trigger,
  TriggerType,
  CloudCodeItem,
} from '../types/AppPanel';
import { BACK4APP_DOT_COM_DASHBOARD_URL } from '../settings';

interface UseCloudTreeResult {
  /** Raw tree from Back4App */
  tree: CloudTreeNode[];
  /** Files from public/ folder (processed) */
  publicFiles: PublicFile[];
  /** Files from cloud/ folder (processed) */
  cloudFiles: ExtractedFile[];
  /** Cloud functions detected from Parse.Cloud.define() calls */
  cloudFunctions: CloudFunction[];
  /** Triggers detected from Parse.Cloud.beforeSave(), etc. */
  triggers: Trigger[];
  /** Unified list of all cloud code items (functions, triggers, jobs) */
  cloudCodeItems: CloudCodeItem[];
  /** Loading state */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Refetch the tree data */
  refetch: () => void;
}

/**
 * All supported Parse trigger types for regex matching
 */
const TRIGGER_TYPES: TriggerType[] = [
  'beforeSave',
  'afterSave',
  'beforeDelete',
  'afterDelete',
  'beforeFind',
  'afterFind',
  'beforeLogin',
  'afterLogin',
  'beforeLogout',
  'afterLogout',
  'beforeSubscribe',
  'beforeConnect',
  'afterEvent',
];

/**
 * Decode base64 content from a CloudTreeNode
 * Format: "data:plain/text;base64,<content>"
 */
function decodeNodeContent(node: CloudTreeNode): string | null {
  if (!node.data?.code) return null;
  
  const code = node.data.code;
  const base64Match = code.match(/base64,(.+)/);
  
  if (base64Match) {
    try {
      return atob(base64Match[1]);
    } catch {
      return null;
    }
  }
  
  return code;
}

/**
 * Remove comments from JavaScript code for parsing purposes only.
 * This is used to detect functions/triggers without matching commented-out code.
 * The original code is preserved for display - this only affects detection.
 * 
 * Handles:
 * - Line comments: // ...
 * - Block comments: /* ... *\/
 * - Preserves strings (doesn't treat // or /* inside strings as comments)
 */
function stripComments(code: string): string {
  let result = '';
  let i = 0;
  
  while (i < code.length) {
    // Check for strings - preserve them completely (don't treat // or /* inside as comments)
    if (code[i] === '"' || code[i] === "'" || code[i] === '`') {
      const quote = code[i];
      result += code[i++];
      
      // Copy string content until closing quote
      while (i < code.length) {
        if (code[i] === '\\' && i + 1 < code.length) {
          // Escape sequence - copy both chars
          result += code[i++];
          result += code[i++];
        } else if (code[i] === quote) {
          result += code[i++];
          break;
        } else {
          result += code[i++];
        }
      }
    }
    // Check for line comment - skip entire line
    else if (code[i] === '/' && code[i + 1] === '/') {
      // Skip until end of line
      while (i < code.length && code[i] !== '\n') {
        i++;
      }
    }
    // Check for block comment - skip entire block
    else if (code[i] === '/' && code[i + 1] === '*') {
      i += 2; // Skip /*
      // Skip until */
      while (i < code.length && !(code[i] === '*' && code[i + 1] === '/')) {
        i++;
      }
      i += 2; // Skip */
    }
    // Regular character - copy as-is
    else {
      result += code[i++];
    }
  }
  
  return result;
}

/**
 * Extract the code block for a specific function/trigger
 * Finds the Parse.Cloud.xxx call and extracts until the matching closing
 */
function extractCodeBlock(fullCode: string, startPattern: RegExp): string {
  const match = startPattern.exec(fullCode);
  if (!match) return '';
  
  const startIndex = match.index;
  let depth = 0;
  let inString = false;
  let stringChar = '';
  let endIndex = startIndex;
  let foundFirstBrace = false;
  
  for (let i = startIndex; i < fullCode.length; i++) {
    const char = fullCode[i];
    const prevChar = i > 0 ? fullCode[i - 1] : '';
    
    // Handle string detection
    if ((char === '"' || char === "'" || char === '`') && prevChar !== '\\') {
      if (!inString) {
        inString = true;
        stringChar = char;
      } else if (char === stringChar) {
        inString = false;
      }
      continue;
    }
    
    if (inString) continue;
    
    // Count braces
    if (char === '{' || char === '(') {
      depth++;
      foundFirstBrace = true;
    } else if (char === '}' || char === ')') {
      depth--;
    }
    
    // Found the end
    if (foundFirstBrace && depth === 0) {
      endIndex = i + 1;
      // Include trailing semicolon and newline if present
      if (fullCode[endIndex] === ';') endIndex++;
      break;
    }
  }
  
  return fullCode.slice(startIndex, endIndex).trim();
}

/**
 * Extract Cloud Functions with their code from file content
 * 
 * @param codeForParsing - Code with comments stripped (for detection)
 * @param originalCode - Original code (for extracting code blocks with comments)
 * @param filePath - Path to the file
 */
function extractFunctionsWithCode(
  codeForParsing: string,
  originalCode: string,
  filePath: string
): CloudCodeItem[] {
  const items: CloudCodeItem[] = [];
  
  // Match Parse.Cloud.define with both single and double quotes
  // Uses code without comments to avoid detecting commented-out functions
  const regex = /Parse\.Cloud\.define\s*\(\s*['"]([^'"]+)['"]/g;
  
  let match;
  while ((match = regex.exec(codeForParsing)) !== null) {
    const functionName = match[1];
    if (functionName && !items.some(f => f.name === functionName)) {
      // Extract the full code block from ORIGINAL code (preserves comments)
      const pattern = new RegExp(
        `Parse\\.Cloud\\.define\\s*\\(\\s*['"]${escapeRegex(functionName)}['"]`
      );
      const functionCode = extractCodeBlock(originalCode, pattern);
      
      items.push({
        id: `function-${functionName}`,
        name: functionName,
        type: 'function',
        filePath,
        code: functionCode || `Parse.Cloud.define('${functionName}', ...)`,
        endpoint: `/functions/${functionName}`,
      });
    }
  }
  
  return items;
}

/**
 * Extract Triggers with their code from file content
 * 
 * @param codeForParsing - Code with comments stripped (for detection)
 * @param originalCode - Original code (for extracting code blocks with comments)
 * @param filePath - Path to the file
 */
function extractTriggersWithCode(
  codeForParsing: string,
  originalCode: string,
  filePath: string
): CloudCodeItem[] {
  const items: CloudCodeItem[] = [];
  
  for (const triggerType of TRIGGER_TYPES) {
    // Uses code without comments to avoid detecting commented-out triggers
    const regex = new RegExp(
      `Parse\\.Cloud\\.${triggerType}\\s*\\(\\s*['"]([^'"]+)['"]`,
      'g'
    );
    
    let match;
    while ((match = regex.exec(codeForParsing)) !== null) {
      const className = match[1];
      const id = `trigger-${triggerType}-${className}`;
      
      if (className && !items.some(t => t.id === id)) {
        // Extract the full code block from ORIGINAL code (preserves comments)
        const pattern = new RegExp(
          `Parse\\.Cloud\\.${triggerType}\\s*\\(\\s*['"]${escapeRegex(className)}['"]`
        );
        const triggerCode = extractCodeBlock(originalCode, pattern);
        
        items.push({
          id,
          name: triggerType,
          type: 'trigger',
          triggerType,
          targetClass: className,
          filePath,
          code: triggerCode || `Parse.Cloud.${triggerType}('${className}', ...)`,
        });
      }
    }
  }
  
  return items;
}

/**
 * Extract Jobs with their code from file content
 * 
 * @param codeForParsing - Code with comments stripped (for detection)
 * @param originalCode - Original code (for extracting code blocks with comments)
 * @param filePath - Path to the file
 */
function extractJobsWithCode(
  codeForParsing: string,
  originalCode: string,
  filePath: string
): CloudCodeItem[] {
  const items: CloudCodeItem[] = [];
  
  // Match Parse.Cloud.job
  // Uses code without comments to avoid detecting commented-out jobs
  const regex = /Parse\.Cloud\.job\s*\(\s*['"]([^'"]+)['"]/g;
  
  let match;
  while ((match = regex.exec(codeForParsing)) !== null) {
    const jobName = match[1];
    if (jobName && !items.some(j => j.name === jobName)) {
      // Extract the full code block from ORIGINAL code (preserves comments)
      const pattern = new RegExp(
        `Parse\\.Cloud\\.job\\s*\\(\\s*['"]${escapeRegex(jobName)}['"]`
      );
      const jobCode = extractCodeBlock(originalCode, pattern);
      
      items.push({
        id: `job-${jobName}`,
        name: jobName,
        type: 'job',
        filePath,
        code: jobCode || `Parse.Cloud.job('${jobName}', ...)`,
      });
    }
  }
  
  return items;
}

/**
 * Escape special regex characters in a string
 */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Recursively extract files from tree and parse their content
 */
function extractFilesWithContent(
  nodes: CloudTreeNode[],
  basePath: string
): {
  files: ExtractedFile[];
  functions: CloudFunction[];
  triggers: Trigger[];
  cloudCodeItems: CloudCodeItem[];
} {
  const files: ExtractedFile[] = [];
  const allFunctions: CloudFunction[] = [];
  const allTriggers: Trigger[] = [];
  const allCloudCodeItems: CloudCodeItem[] = [];

  function traverse(nodes: CloudTreeNode[], currentPath: string) {
    for (const node of nodes) {
      const nodePath = `${currentPath}/${node.text}`;

      if (node.type === 'folder' && node.children) {
        traverse(node.children, nodePath);
      } else if (!node.type) {
        // It's a file
        const ext = node.text.includes('.') 
          ? node.text.split('.').pop()?.toLowerCase() || ''
          : '';
        
        files.push({
          name: node.text,
          path: nodePath,
          type: getFileType(ext),
        });

        // Parse JS files for functions, triggers, and jobs
        if (ext === 'js' || ext === 'mjs') {
          const originalCode = decodeNodeContent(node);
          if (originalCode) {
            // Strip comments for detection only - this prevents
            // commented-out code from being detected as active functions
            const codeForParsing = stripComments(originalCode);
            
            // Extract functions (detect in clean code, extract from original)
            const functionItems = extractFunctionsWithCode(codeForParsing, originalCode, nodePath);
            functionItems.forEach(item => {
              allCloudCodeItems.push(item);
              allFunctions.push({
                name: item.name,
                filePath: item.filePath,
              });
            });
            
            // Extract triggers (detect in clean code, extract from original)
            const triggerItems = extractTriggersWithCode(codeForParsing, originalCode, nodePath);
            triggerItems.forEach(item => {
              allCloudCodeItems.push(item);
              allTriggers.push({
                type: item.triggerType!,
                className: item.targetClass!,
                filePath: item.filePath,
              });
            });
            
            // Extract jobs (detect in clean code, extract from original)
            const jobItems = extractJobsWithCode(codeForParsing, originalCode, nodePath);
            allCloudCodeItems.push(...jobItems);
          }
        }
      }
    }
  }

  traverse(nodes, basePath);
  return {
    files,
    functions: allFunctions,
    triggers: allTriggers,
    cloudCodeItems: allCloudCodeItems,
  };
}

/**
 * Map file extension to ExtractedFile type
 */
function getFileType(ext: string): ExtractedFile['type'] {
  switch (ext) {
    case 'html':
    case 'htm':
      return 'html';
    case 'js':
    case 'mjs':
      return 'js';
    case 'css':
      return 'css';
    case 'json':
      return 'json';
    default:
      return 'other';
  }
}

/**
 * Hook to fetch and process the cloud code tree from Back4App
 * 
 * @param appId - The Parse App ID (Dashboard ID)
 * @returns Cloud tree data with processed files, functions, triggers, and unified items
 */
export function useCloudTree(appId?: string): UseCloudTreeResult {
  const [tree, setTree] = useState<CloudTreeNode[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTree = useCallback(async () => {
    if (!appId) {
      setTree([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${BACK4APP_DOT_COM_DASHBOARD_URL}/parse-app/${appId}/cloud`,
        { 
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
          },
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          setTree([]);
          return;
        }
        if (response.status === 401 || response.status === 403) {
          throw new Error('Not authorized to access this app');
        }
        throw new Error(`Failed to fetch cloud tree: ${response.status}`);
      }

      const data: CloudTreeResponse = await response.json();
      setTree(data.tree || []);
    } catch (err) {
      console.error('Error fetching cloud tree:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setTree([]);
    } finally {
      setIsLoading(false);
    }
  }, [appId]);

  useEffect(() => {
    fetchTree();
  }, [fetchTree]);

  // Process public files from tree
  const publicFiles = useMemo((): PublicFile[] => {
    const publicNode = tree.find(n => n.text === 'public');
    if (!publicNode?.children) return [];
    
    const { files } = extractFilesWithContent(publicNode.children, 'public');
    return files.map(f => ({
      name: f.name,
      path: f.path,
      type: f.type,
    }));
  }, [tree]);

  // Process cloud files, functions, triggers, and unified items from tree
  const { cloudFiles, cloudFunctions, triggers, cloudCodeItems } = useMemo(() => {
    const cloudNode = tree.find(n => n.text === 'cloud');
    if (!cloudNode?.children) {
      return {
        cloudFiles: [],
        cloudFunctions: [],
        triggers: [],
        cloudCodeItems: [],
      };
    }
    
    const result = extractFilesWithContent(cloudNode.children, 'cloud');
    
    return {
      cloudFiles: result.files,
      cloudFunctions: result.functions,
      triggers: result.triggers,
      cloudCodeItems: result.cloudCodeItems,
    };
  }, [tree]);

  return {
    tree,
    publicFiles,
    cloudFiles,
    cloudFunctions,
    triggers,
    cloudCodeItems,
    isLoading,
    error,
    refetch: fetchTree,
  };
}

export default useCloudTree;
